import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ChevronDown, MapPin, TrendingUp, Wallet, Percent, Ruler, Building2, Loader2, Mail,
  CheckCircle2, AlertTriangle, Target, Rocket, ShieldCheck, Lightbulb, Compass,
  Hammer, Layers, LogOut, FileText, Eye, Zap, Split, Scale, BarChart3, Tag, DollarSign,
  TrendingDown, Home,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO = "/logo.png";

function Collapse({ title, defaultOpen = false, testid, children, right, icon: Icon }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <span className="flex items-center gap-2.5">
          {Icon && <Icon className="h-4 w-4 text-[#27BEAA]" />}
          <span className="text-sm font-semibold tracking-wide uppercase text-white/90">{title}</span>
        </span>
        <span className="flex items-center gap-3">
          {right && <span className="text-[11px] uppercase tracking-wide text-white/40">{right}</span>}
          <ChevronDown className={`h-4 w-4 text-[#27BEAA] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/10 pt-4">{children}</div>}
    </div>
  );
}

function RowGrid({ rows }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-white/40">Not provided.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
      {rows.map((r, i) => (
        <div key={i} className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wider text-white/40">{r.label}</span>
          <span className="text-sm text-white/90 mt-0.5">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, rows, defaultOpen = false, testid, icon }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Collapse title={title} defaultOpen={defaultOpen} testid={testid} icon={icon}>
      <RowGrid rows={rows} />
    </Collapse>
  );
}

function GroupedSection({ title, groups, fallbackRows, defaultOpen = false, testid, icon }) {
  const hasGroups = Array.isArray(groups) && groups.some((g) => g.rows && g.rows.length);
  if (!hasGroups) return <Section title={title} rows={fallbackRows} defaultOpen={defaultOpen} testid={testid} icon={icon} />;
  return (
    <Collapse title={title} defaultOpen={defaultOpen} testid={testid} icon={icon}>
      <div className="space-y-5">
        {groups.filter((g) => g.rows && g.rows.length).map((g, i) => (
          <div key={i} data-testid={`${testid}-group-${i}`}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#27BEAA] mb-2.5">{g.title}</div>
            <RowGrid rows={g.rows} />
          </div>
        ))}
      </div>
    </Collapse>
  );
}

function BriefCard({ icon: Icon, title, body, bullets, testid, accent, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const hasBody = body && String(body).trim();
  const hasBullets = bullets && bullets.length > 0;
  if (!hasBody && !hasBullets) return null;
  return (
    <div className={`rounded-2xl border overflow-hidden ${accent ? "border-[#27BEAA]/30 bg-[#27BEAA]/[0.05]" : "border-white/10 bg-white/[0.02]"}`} data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 sm:px-7 py-5 text-left hover:bg-white/[0.03] transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <span className="flex items-center gap-2.5">
          <Icon className="h-4 w-4 text-[#27BEAA]" />
          <span className="text-sm font-semibold uppercase tracking-wide text-white/90">{title}</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-[#27BEAA] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 sm:px-7 pb-6 border-t border-white/10 pt-4">
          {hasBody && <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{body}</p>}
          {hasBullets && (
            <ul className={`space-y-2 ${hasBody ? "mt-3" : ""}`}>
              {bullets.map((b, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-white/80 leading-relaxed">
                  <span className="text-[#27BEAA] mt-1.5 text-[8px]">●</span><span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4">
      <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
        <Icon className="h-3.5 w-3.5 text-[#27BEAA] shrink-0 mt-0.5" />{label}
      </div>
      <div className="text-2xl font-bold text-white mt-1.5 tracking-tight">{value}</div>
    </div>
  );
}

function LandStoryCard({ label, value, sub, highlight }) {
  return (
    <div className={`rounded-xl border px-5 py-4 ${highlight ? "border-[#27BEAA]/40 bg-[#27BEAA]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">{label}</div>
      <div className={`text-2xl font-bold mt-1.5 tracking-tight ${highlight ? "text-[#27BEAA]" : "text-white"}`}>{value}</div>
      {sub && <div className="text-[11px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function Metric2060({ label, value, pct, target, over, accent, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4" data-testid={testid}>
      <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
        <Percent className="h-3.5 w-3.5 text-[#27BEAA] shrink-0 mt-0.5" />{label}
      </div>
      <div className={`text-2xl font-bold mt-1.5 tracking-tight ${accent ? "text-[#27BEAA]" : "text-white"}`}>{value}</div>
      <div className="mt-1 text-[11px]">
        <span className={`font-semibold ${over ? "text-amber-400" : "text-[#27BEAA]"}`}>{pct}</span>
        <span className="text-white/40"> of GDV · target {target}</span>
      </div>
    </div>
  );
}

function ScenarioCard({ s, highlight }) {
  const Icon = s.label === "Upside" ? Rocket : s.label === "Target" ? Target : s.label === "Base Case" ? TrendingUp : ShieldCheck;
  return (
    <div className={`rounded-xl border p-5 ${highlight ? "border-[#27BEAA]/40 bg-[#27BEAA]/[0.06]" : "border-white/10 bg-white/[0.02]"}`} data-testid={`scenario-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start gap-2 text-white/60 text-[11px] uppercase tracking-wider leading-tight min-h-[2.4em]">
        <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${highlight ? "text-[#27BEAA]" : "text-white/50"}`} />{s.label}
      </div>
      <div className="flex items-baseline gap-2 mt-2 flex-nowrap">
        <span className={`text-3xl font-bold tracking-tight ${highlight ? "text-[#27BEAA]" : "text-white"}`}>{s.margin_pct}</span>
        <span className="text-xs text-white/40 whitespace-nowrap">margin</span>
      </div>
      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-white/40">Net Profit</span><span className="text-white/90 font-medium">{s.net_profit}</span></div>
        <div className="flex justify-between"><span className="text-white/40">Implied All-In</span><span className="text-white/70">{s.all_in}</span></div>
        {s.additional_profit !== "—" && (
          <div className="flex justify-between"><span className="text-white/40">Upside vs Floor</span><span className="text-[#27BEAA] font-medium">+{s.additional_profit}</span></div>
        )}
      </div>
      <p className="text-[11px] text-white/40 mt-3 leading-snug">{s.note}</p>
    </div>
  );
}

// ===================== Exit Strategy Snapshot =====================
// Presentation layer over record.exit_strategy_snapshot — the numbers are computed by the
// Property Analysis / Exit Analysis engine (single source of truth). No math is done here.
const SNAP_ICON = { land_flip: Tag, entitle: Layers, subdivide: Split, build_sfr: Home };
const SNAP_ORDER = ["land_flip", "entitle", "subdivide", "build_sfr"];
const snapMoney = (v) => (v == null || isNaN(v) ? "—" : "$" + Math.round(v).toLocaleString());
const snapPct = (v) => (v == null || isNaN(v) ? "—" : Math.round(v) + "%");
const snapRoi = (v) => (v == null || isNaN(v) ? "—" : Math.round(v * 100) + "%");
// A negative Max Land Offer means the strategy can't support any land cost — show plainly.
const snapLandOffer = (v) => (v == null || isNaN(v) ? "—" : v < 0 ? "Not viable" : "$" + Math.round(v).toLocaleString());
const snapSqft = (v) => (v == null || isNaN(v) ? "—" : Math.round(v).toLocaleString());

function BigMetric({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className={`rounded-xl border px-5 py-4 ${accent ? "border-[#27BEAA]/40 bg-[#27BEAA]/[0.07]" : "border-white/10 bg-white/[0.02]"}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.2em]">
        {Icon && <Icon className="h-3.5 w-3.5 text-[#27BEAA] shrink-0" />}{label}
      </div>
      <div className={`text-2xl font-bold mt-1.5 tracking-tight ${accent ? "text-[#27BEAA]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function StrategyTab({ s, active, isPrimary, onClick }) {
  const Icon = SNAP_ICON[s.key] || Layers;
  return (
    <button onClick={onClick} data-testid={`exit-strategy-tab-${s.key}`}
      className={`flex-1 min-w-[150px] text-left rounded-xl border px-4 py-3 transition-colors ${active ? "border-[#27BEAA]/70 bg-[#27BEAA]/[0.1]" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.045]"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 min-w-0">
          <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#27BEAA]" : "text-white/50"}`} />
          <span className={`text-xs font-semibold uppercase tracking-wide truncate ${active ? "text-white" : "text-white/70"}`}>{s.label}</span>
        </span>
        {isPrimary && <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#27BEAA]/20 text-[#27BEAA] shrink-0" data-testid={`exit-tab-primary-${s.key}`}>Primary</span>}
      </div>
      <div className="mt-2 text-lg font-bold tracking-tight text-[#27BEAA]" data-testid={`exit-strategy-tab-profit-${s.key}`}>{snapMoney(s.profit)}</div>
      <div className="text-[10px] uppercase tracking-wide text-white/40">projected profit</div>
    </button>
  );
}

function LotEconomicsTable({ s }) {
  const isVert = s.key === "build_sfr";
  const t = s.lot_totals || {};
  return (
    <div data-testid="exit-lot-economics">
      <div className="flex items-center gap-2 mb-3">
        <Split className="h-4 w-4 text-[#27BEAA]" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-white/90">Lot-Level Economics</h3>
        <span className="text-[11px] text-white/40">· {s.lots} resulting lots</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-white/40 border-b border-white/10">
              <th className="px-4 py-3 font-medium">Lot</th>
              {isVert ? (
                <>
                  <th className="px-4 py-3 font-medium text-right">Lot Size</th>
                  <th className="px-4 py-3 font-medium">Home / Product</th>
                  <th className="px-4 py-3 font-medium text-right">Home SF</th>
                  <th className="px-4 py-3 font-medium text-right">Build Cost</th>
                  <th className="px-4 py-3 font-medium text-right">Finished Value</th>
                  <th className="px-4 py-3 font-medium text-right">Target Sale</th>
                  <th className="px-4 py-3 font-medium text-right">Profit</th>
                </>
              ) : (
                <>
                  <th className="px-4 py-3 font-medium text-right">Size (SF)</th>
                  <th className="px-4 py-3 font-medium text-right">Frontage</th>
                  <th className="px-4 py-3 font-medium text-right">{s.key === "subdivide" ? "Build-Ready Value" : "Pad-Ready Value"}</th>
                  <th className="px-4 py-3 font-medium text-right">Target Sale</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {(s.lot_rows || []).map((l) => (
              <tr key={l.id} className="border-b border-white/5 last:border-0" data-testid={`exit-lot-row-${s.key}-${l.id}`}>
                <td className="px-4 py-2.5 text-white/90 font-medium">Lot {l.id}</td>
                {isVert ? (
                  <>
                    <td className="px-4 py-2.5 text-right text-white/70">{snapSqft(l.size)}</td>
                    <td className="px-4 py-2.5 text-white/70">{l.product || "—"}</td>
                    <td className="px-4 py-2.5 text-right text-white/70">{snapSqft(l.home_sqft)}</td>
                    <td className="px-4 py-2.5 text-right text-white/70">{snapMoney(l.build_cost)}</td>
                    <td className="px-4 py-2.5 text-right text-[#27BEAA]">{snapMoney(l.finished_value)}</td>
                    <td className="px-4 py-2.5 text-right text-white/70">{snapMoney(l.target)}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-400">{snapMoney(l.profit)}</td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5 text-right text-white/70">{snapSqft(l.size)}</td>
                    <td className="px-4 py-2.5 text-right text-white/70">{l.frontage ? `${l.frontage} ft` : "—"}</td>
                    <td className="px-4 py-2.5 text-right text-[#27BEAA]">{snapMoney(l.value)}</td>
                    <td className="px-4 py-2.5 text-right text-white/70">{snapMoney(l.target)}</td>
                    <td className="px-4 py-2.5 text-white/50 text-[12px]">{l.status}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-white/20 font-semibold">
              <td className="px-4 py-3 text-white uppercase text-[11px] tracking-wider" colSpan={isVert ? 4 : 3}>Totals</td>
              {isVert ? (
                <>
                  <td className="px-4 py-3 text-right text-white/90" data-testid="exit-lot-total-buildcost">{snapMoney(t.total_build_cost)}</td>
                  <td className="px-4 py-3 text-right text-[#27BEAA]" data-testid="exit-lot-total-gdv">{snapMoney(t.total_value)}</td>
                  <td className="px-4 py-3 text-right text-white/90" />
                  <td className="px-4 py-3 text-right text-emerald-400" data-testid="exit-lot-total-profit">{snapMoney(t.total_profit)}</td>
                </>
              ) : (
                <>
                  <td className="px-4 py-3 text-right text-[#27BEAA]" data-testid="exit-lot-total-value">{snapMoney(t.total_value)}</td>
                  <td className="px-4 py-3 text-right text-white/90" data-testid="exit-lot-total-target">{snapMoney(t.total_target)}</td>
                  <td className="px-4 py-3" />
                </>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function StrategyDetail({ s, isPrimary }) {
  const Icon = SNAP_ICON[s.key] || Layers;
  const multi = s.lots != null && s.lots > 1;
  const isVert = s.key === "build_sfr";
  const viable = s.status_label === "Viable";
  const keyMetrics = [
    { label: s.revenue_label || "Exit Value", value: snapMoney(s.exit_value), icon: TrendingUp, slug: "exit-value" },
    { label: "Capital Required", value: snapMoney(s.capital_required), icon: Wallet, slug: "capital-required" },
    { label: "Projected Profit", value: snapMoney(s.profit), icon: DollarSign, slug: "projected-profit", accent: true },
    { label: "ROI", value: snapRoi(s.roi), icon: Percent, slug: "roi" },
    { label: "Margin", value: snapPct(s.margin_pct), icon: BarChart3, slug: "margin" },
    { label: "Timeline", value: s.timeline_months ? `${s.timeline_months} mo` : "—", icon: Compass, slug: "timeline" },
    { label: s.key === "land_flip" ? "Flip Max Offer" : "Max Land Offer", value: snapLandOffer(s.land_basis), icon: Target, slug: "max-land-offer" },
  ];
  const jv = s.jv;
  return (
    <div className="space-y-6" data-testid="exit-strategy-detail">
      {/* Header */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="exit-detail-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <Icon className="h-5 w-5 text-[#27BEAA]" />
              <h2 className="text-2xl font-bold tracking-tight text-white" data-testid="exit-detail-name">{s.label}</h2>
              {isPrimary && <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#27BEAA]/20 text-[#27BEAA]" data-testid="exit-detail-primary">Primary Strategy</span>}
            </div>
            <p className="text-sm text-white/55 mt-2 max-w-2xl leading-relaxed">{s.description}</p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {multi && (
              <span className="text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full bg-[#27BEAA]/15 text-[#27BEAA]" data-testid="exit-detail-yield">
                {isVert ? `${s.homes} Homes · ${s.lots} Lots` : `${s.lots} Resulting Lots`}
              </span>
            )}
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full ${viable ? "bg-[#27BEAA]/15 text-[#27BEAA]" : "bg-amber-500/15 text-amber-400"}`} data-testid="exit-detail-status">
              {viable ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}{s.status_label}
            </span>
          </div>
        </div>
        {s.emphasis && s.emphasis.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {s.emphasis.map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wide px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02] text-white/50">{t}</span>
            ))}
          </div>
        )}
      </div>

      {/* Key economics */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-3">Key Economics</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="exit-detail-key-economics">
          {keyMetrics.map((m) => (
            <BigMetric key={m.slug} label={m.label} value={m.value} icon={m.icon} accent={m.accent} testid={`exit-metric-${m.slug}`} />
          ))}
        </div>
      </div>

      {/* Vertical project totals */}
      {isVert && s.project && (
        <div data-testid="exit-detail-project">
          <div className="text-[11px] uppercase tracking-[0.15em] text-[#27BEAA] mb-3">Total Project · {s.project.homes} Homes</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            <BigMetric label="Total GDV" value={snapMoney(s.project.total_gdv)} accent testid="exit-project-gdv" />
            <BigMetric label="Total Development Cost" value={snapMoney(s.project.project_all_in)} testid="exit-project-devcost" />
            <BigMetric label="Project Profit" value={snapMoney(s.project.project_profit)} testid="exit-project-profit" />
            <BigMetric label="Project Max Land" value={snapLandOffer(s.project.project_max_land)} testid="exit-project-maxland" />
          </div>
          <p className="text-[11px] text-white/40 mt-2">Totals combine all {s.project.homes} homes (land purchased once). Avg home {snapSqft(s.project.avg_home_size)} SF · {snapSqft(s.project.total_buildable_sf)} SF total buildable.</p>
        </div>
      )}

      {/* Development & exit detail */}
      {s.cost_lines && s.cost_lines.length > 0 && (
        <div data-testid="exit-detail-costs">
          <div className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-3">Development &amp; Exit Detail</div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <div className="divide-y divide-white/5">
              {s.cost_lines.map((c, i) => (
                <div key={i} className="flex justify-between py-2 text-sm">
                  <span className="text-white/55">{c.label}</span>
                  <span className="text-white/90 font-medium">{snapMoney(c.value)}</span>
                </div>
              ))}
              <div className="flex justify-between py-2.5 text-sm font-semibold">
                <span className="text-white uppercase text-[11px] tracking-wider">Total {isVert ? "Development" : "Non-Land"} Cost</span>
                <span className="text-[#27BEAA]">{snapMoney(s.total_cost)}</span>
              </div>
            </div>
            {s.extras && s.extras.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
                {s.extras.map((e, i) => (
                  <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-white/40">{e.label}</div>
                    <div className="text-sm font-semibold text-white mt-0.5">{typeof e.value === "number" ? e.value.toLocaleString() : (e.value ?? "—")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lot-level economics */}
      {multi && s.lot_rows && s.lot_rows.length > 0 && <LotEconomicsTable s={s} />}

      {/* JV economics */}
      {jv && (
        <div data-testid="exit-jv">
          <div className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-3">JV Economics — Land Contribution</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <BigMetric label="Project Profit" value={snapMoney(jv.project_profit)} accent testid="exit-jv-project-profit" />
            <BigMetric label={`Landowner / Seller (${jv.split.landowner}%)`} value={snapMoney(jv.landowner)} testid="exit-jv-landowner" />
            <BigMetric label={`Investor (${jv.split.investor}%)`} value={snapMoney(jv.investor)} testid="exit-jv-investor" />
            <BigMetric label={`Arqitek (${jv.split.arqitek}%)`} value={snapMoney(jv.arqitek)} testid="exit-jv-arqitek" />
            <BigMetric label="Arqitek Cash Req." value={snapMoney(jv.arqitek_cash)} testid="exit-jv-cash" />
            <BigMetric label="Arqitek ROI" value={snapRoi(jv.arqitek_roi)} testid="exit-jv-roi" />
          </div>
        </div>
      )}

      {/* Angela insight */}
      {s.insight_text && (
        <div className="rounded-2xl border border-[#27BEAA]/30 bg-[#27BEAA]/[0.05] p-5" data-testid="exit-detail-insight">
          <div className="flex items-center gap-2 mb-2.5">
            <Lightbulb className="h-4 w-4 text-[#27BEAA]" />
            <span className="text-sm font-semibold uppercase tracking-wide text-white/90">Angela — {s.label}</span>
          </div>
          <p className="text-sm text-white/80 leading-relaxed">{s.insight_text}</p>
        </div>
      )}
    </div>
  );
}

function ExitSnapshotSection({ snap }) {
  const byKey = {};
  (snap.strategies || []).forEach((s) => { byKey[s.key] = s; });
  const ordered = SNAP_ORDER.map((k) => byKey[k]).filter(Boolean);
  const primary = snap.primary_strategy;
  const defaultKey = primary || (ordered.find((s) => s.profit != null) || ordered[0] || {}).key;
  const [sel, setSel] = useState(defaultKey);
  if (ordered.length === 0) return null;
  const current = byKey[sel] || ordered[0];
  return (
    <section className="space-y-5" data-testid="exit-strategy-snapshot">
      <div>
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-[#27BEAA]" />
          <h2 className="text-lg font-semibold tracking-tight">Exit Strategy</h2>
          {snap.subdivision_active && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#27BEAA]/15 text-[#27BEAA]" data-testid="exit-snapshot-lots-badge">{snap.potential_lots} Lots</span>
          )}
        </div>
        <p className="text-sm text-white/50 mt-1">Select how Arqitek exits this deal to see the full economics of that stage.</p>
      </div>

      {/* Strategy selector */}
      <div className="flex flex-wrap gap-3" data-testid="exit-strategy-selector">
        {ordered.map((s) => (
          <StrategyTab key={s.key} s={s} active={current.key === s.key} isPrimary={primary === s.key} onClick={() => setSel(s.key)} />
        ))}
      </div>

      {/* Selected strategy full-width view */}
      <StrategyDetail s={current} isPrimary={primary === current.key} />
    </section>
  );
}

export default function InvestorPortal() {
  const { token } = useParams();
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "ARQITEK — Investment Opportunity";
    let timer;
    const fetchDeal = () => axios
      .get(`${BACKEND}/api/public/deal/${token}`)
      .then((r) => {
        setDeal(r.data);
        // While Angela is still writing the memo, poll until it's ready.
        if (r.data?.brief_status === "generating") {
          timer = setTimeout(fetchDeal, 8000);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    fetchDeal();
    return () => clearTimeout(timer);
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08100E] flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#27BEAA] animate-spin" />
      </div>
    );
  }
  if (error || !deal) {
    return (
      <div className="min-h-screen bg-[#08100E] flex flex-col items-center justify-center text-center px-6" data-testid="portal-not-found">
        <img src={LOGO} alt="ARQITEK" className="h-8 mb-6 opacity-90" />
        <h1 className="text-xl font-semibold text-white">This deal link is unavailable</h1>
        <p className="text-white/50 mt-2 text-sm">The link may have expired or is incorrect. Please contact ARQITEK.</p>
      </div>
    );
  }

  const m = deal.metrics || {};
  const h = deal.highlights || null;
  const p2 = deal.pad_2060 || null;
  const b = deal.brief || {};
  const uw = deal.underwriting || {};
  const scenarios = deal.return_scenarios || [];
  const ls = deal.land_story || null;
  const snap = deal.exit_strategy_snapshot || null;

  return (
    <div className="min-h-screen bg-[#08100E] text-white" data-testid="investor-portal-page">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-5">
          <img src={LOGO} alt="ARQITEK" className="h-6 w-auto object-contain" data-testid="portal-logo" />
          <span className="block mt-2 text-[11px] uppercase tracking-[0.2em] text-white/40">Investment Opportunity</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Title */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="portal-deal-name">{deal.deal_name}</h1>
          {deal.location && (
            <div className="flex items-center gap-1.5 text-white/60 mt-3">
              <MapPin className="h-4 w-4 text-[#27BEAA]" /> <span className="text-base">{deal.location}</span>
            </div>
          )}
        </div>

        {/* Exit Strategy Snapshot — true economics of all four exits, straight from the Analysis */}
        {snap && snap.strategies && snap.strategies.length > 0 && (
          <ExitSnapshotSection snap={snap} />
        )}

        {/* Land Value Story — the max land-offer picture from the decision engine */}
        {ls && ls.cards && (
          <section data-testid="portal-land-story" className="space-y-4">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[#27BEAA]" />
              <h2 className="text-lg font-semibold tracking-tight">Land Offer</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="portal-land-story-cards">
              {ls.cards.filter((c) => ["market-value", "max-supported", "opening"].includes(c.key)).map((c) => (
                <LandStoryCard key={c.key} label={c.label} value={c.value} sub={c.sub} highlight={c.key === "max-supported"} />
              ))}
            </div>
          </section>
        )}

        {/* Underwriting section — titled, with the 20/60/20 badge belonging to it */}
        <div className="space-y-4" data-testid="portal-underwriting-section">
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-[#27BEAA]" />
              <h2 className="text-lg font-semibold tracking-tight">20/60/20 Underwriting</h2>
            </div>
            {(p2 || typeof m.pass === "boolean") && (() => {
              const passes = p2 ? p2.profit_ok : m.pass;
              return (
              <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${passes ? "bg-[#27BEAA]/15 text-[#27BEAA]" : "bg-amber-500/15 text-amber-400"}`} data-testid="portal-underwriting-badge">
                {passes ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                {passes ? "Passes the 20/60/20 Rule" : "Review the 20/60/20 Rule"}
              </div>
              );
            })()}
          </div>

        {/* Key metrics — row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portal-metrics">
          <Metric icon={TrendingUp} label="GDV" value={m.arv} />
          <Metric icon={Building2} label="Proposed Home Size" value={m.living_sqft} />
          <Metric icon={Home} label="Proposed Beds / Baths" value={h ? h.beds_baths : "—"} />
        </div>

        {/* Zoning · Current Listed Price · Pad-Ready Cost — row 2 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="portal-summary-row">
          <Metric icon={Scale} label="Zoning" value={h ? h.zoning : "—"} />
          <Metric icon={Tag} label="Current Listed Price" value={m.listed_price} />
          <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4" data-testid="portal-highlight-pad-ready">
            <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
              <DollarSign className="h-3.5 w-3.5 text-[#27BEAA] shrink-0 mt-0.5" />Estimated Pad-Ready Cost
            </div>
            <div className="text-2xl font-bold text-white mt-1.5 tracking-tight">{h ? h.pad_ready_cost : "—"}</div>
            {h && <div className="text-[11px] text-white/40 mt-1">{h.pad_ready_note}</div>}
          </div>
        </div>

        {/* Target allocation (20/60/20 of GDV) — row 3 */}
        {p2 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="portal-targets">
            <Metric icon={Target} label="Target Land + Pad-Ready (20% GDV)" value={p2.target_land} />
            <Metric icon={Target} label="Target All-In Construction (60% GDV)" value={p2.target_construction} />
            <Metric icon={Target} label="Target Profit (20% GDV)" value={p2.target_profit} />
          </div>
        )}

        {/* Actual 20/60/20 — row 4 */}
        {p2 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="portal-2060">
            <Metric2060 testid="portal-2060-land" label="Actual Land + Pad-Ready Cost" value={p2.land} pct={p2.land_pct} target="20%" over={p2.land_over} accent />
            <Metric2060 testid="portal-2060-construction" label="Actual All-In Construction Cost" value={p2.construction} pct={p2.construction_pct} target="60%" over={p2.construction_over} accent />
            <Metric2060 testid="portal-2060-profit" label="Actual Profit" value={p2.profit} pct={p2.profit_pct} target="20%" over={!p2.profit_ok} accent />
          </div>
        )}

        {/* Solve — only when the 20/60/20 rule doesn't pass: what the land must cost */}
        {p2 && p2.solve && (
          <div data-testid="portal-2060-solve">
            <div className="text-[11px] uppercase tracking-[0.15em] text-amber-400/80 mb-2 pl-1">To meet the 20/60/20 rule</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4" data-testid="solve-max-pad-ready">
                <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
                  <Target className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />Max Pad-Ready Land (20% GDV)
                </div>
                <div className="text-2xl font-bold text-white mt-1.5 tracking-tight">{p2.solve.max_pad_ready_land}</div>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4" data-testid="solve-max-land-offer">
                <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
                  <Target className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />Max Land Offer Price
                </div>
                <div className="text-2xl font-bold text-amber-400 mt-1.5 tracking-tight">{p2.solve.max_land_offer_negative ? "Not feasible" : p2.solve.max_land_offer}</div>
                <div className="text-[11px] text-white/40 mt-1">= 20% of GDV − Site Work</div>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4" data-testid="solve-reduction">
                <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
                  <Target className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />Reduction From List Price
                </div>
                <div className="text-2xl font-bold text-white mt-1.5 tracking-tight">{p2.solve.reduction_from_list}</div>
                <div className="text-[11px] text-white/40 mt-1">List {p2.solve.current_list} → {p2.solve.max_land_offer_negative ? "$0" : p2.solve.max_land_offer}</div>
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Land adjustment callout — acquired below the 15%-of-GDV budget */}
        {deal.land_adjustment && deal.land_adjustment.adjusted && (
          <div
            className="rounded-xl border border-[#27BEAA]/30 bg-[#27BEAA]/[0.06] px-5 py-4 flex items-start gap-3"
            data-testid="portal-land-adjustment"
          >
            <TrendingDown className="h-5 w-5 text-[#27BEAA] mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-white">
                Adjusted — land acquired at list price, saved {deal.land_adjustment.savings}
              </div>
              <div className="text-[13px] text-white/50 mt-1 leading-relaxed">
                The property is listed at {deal.land_adjustment.listed}, below our {deal.land_adjustment.budget} land
                budget (15% of GDV). We underwrite the land at the {deal.land_adjustment.applied} list price, which
                lifts projected profit accordingly.
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {deal.brief_status === "generating" && (
            <div className="flex items-center gap-3 rounded-xl border border-[#27BEAA]/20 bg-[#27BEAA]/[0.06] px-5 py-4" data-testid="portal-brief-generating">
              <Loader2 className="h-4 w-4 text-[#27BEAA] animate-spin shrink-0" />
              <span className="text-sm text-white/70">Angela is preparing the full deal memo — the analysis sections will appear here automatically in a moment.</span>
            </div>
          )}
          <BriefCard icon={FileText} title="Executive Overview" body={b.executive_overview} testid="brief-overview" />
          <BriefCard icon={Compass} title="Location & Market" body={b.location_market} testid="brief-location" />
          <BriefCard icon={Building2} title="Development Plan & Exit Strategy" body={b.development_plan} testid="brief-development" />
          <BriefCard icon={Zap} title="Utilities Assessment" bullets={b.utilities_assessment} testid="brief-utilities" />
          <BriefCard icon={AlertTriangle} title="Risk Factors & Diligence" bullets={b.risk_factors} testid="brief-risks" />

          {/* Cost per square foot */}
          {deal.cost_per_sqft_detail && (
            <Collapse title="Cost Per Square Foot" testid="portal-cost-psf" icon={Ruler}
              right={deal.cost_per_sqft_detail.tier || undefined}>
              {deal.cost_per_sqft_detail.has_data ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/40">All-In / SF</div>
                    <div className="text-xl font-bold text-white mt-1" data-testid="psf-all-in">{deal.cost_per_sqft_detail.all_in_psf}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/40">Hard Construction / SF</div>
                    <div className="text-xl font-bold text-white mt-1" data-testid="psf-construction">{deal.cost_per_sqft_detail.construction_psf}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/40">GDV / SF</div>
                    <div className="text-xl font-bold text-[#27BEAA] mt-1" data-testid="psf-arv">{deal.cost_per_sqft_detail.arv_psf}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-white/40">Home Size</div>
                    <div className="text-xl font-bold text-white mt-1" data-testid="psf-living">{deal.cost_per_sqft_detail.living_sqft}</div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white/40" data-testid="psf-empty">Add a Proposed Home Size on the analysis to populate cost-per-square-foot metrics.</p>
              )}
            </Collapse>
          )}

          {/* Construction line items */}
          {deal.construction_lines && deal.construction_lines.length > 0 && (
            <Collapse title="Hard Construction Breakdown" testid="portal-construction" icon={Hammer}
              right={uw.has_quotes ? "Sum of quotes received" : "Budgeted"}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-white/40 border-b border-white/10">
                      <th className="py-2 font-medium">Line Item</th>
                      <th className="py-2 font-medium text-right">% of Budget</th>
                      <th className="py-2 font-medium text-right">Budget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deal.construction_lines.map((c, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="py-2 text-white/90">{c.label}</td>
                        <td className="py-2 text-right text-white/50">{c.budget_pct}</td>
                        <td className="py-2 text-right text-white/90">{c.budget}</td>
                      </tr>
                    ))}
                  </tbody>
                  {deal.construction_total && (
                    <tfoot>
                      <tr className="border-t border-white/20" data-testid="portal-construction-total">
                        <td className="py-2.5 text-white font-semibold uppercase text-[11px] tracking-wider">Total</td>
                        <td className="py-2.5 text-right text-white/50">100%</td>
                        <td className="py-2.5 text-right text-[#27BEAA] font-bold">{deal.construction_total}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </Collapse>
          )}

          {/* Property sections */}
          {deal.subdivision && deal.subdivision.enabled && (
            <Collapse title={`Subdivision · ${deal.subdivision.count} Lots`} testid="portal-subdivision" icon={Split}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                {deal.subdivision.summary.map((s, i) => (
                  <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={`subdivision-summary-${i}`}>
                    <div className="text-[10px] uppercase tracking-wider text-white/40">{s.label}</div>
                    <div className="text-base font-bold text-white mt-1">{s.value}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full text-sm" data-testid="subdivision-lots-table">
                  <thead>
                    <tr className="text-left text-white/40 text-[11px] uppercase tracking-wider border-b border-white/10">
                      <th className="px-4 py-2.5 font-medium">Lot</th>
                      <th className="px-4 py-2.5 font-medium">Type</th>
                      <th className="px-4 py-2.5 font-medium text-right">GDV</th>
                      <th className="px-4 py-2.5 font-medium text-right">All-In</th>
                      <th className="px-4 py-2.5 font-medium text-right">Profit</th>
                      <th className="px-4 py-2.5 font-medium text-right">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deal.subdivision.lots.map((l, i) => (
                      <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`subdivision-lot-row-${i}`}>
                        <td className="px-4 py-2.5 text-white/90 font-medium">{l.name}</td>
                        <td className="px-4 py-2.5 text-white/50">{l.type}</td>
                        <td className="px-4 py-2.5 text-right text-white/80">{l.arv}</td>
                        <td className="px-4 py-2.5 text-right text-white/80">{l.all_in}</td>
                        <td className="px-4 py-2.5 text-right text-[#27BEAA]">{l.profit}</td>
                        <td className="px-4 py-2.5 text-right text-white/80">{l.margin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {deal.subdivision.site_costs.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Shared Site / Horizontal Costs</div>
                  <RowGrid rows={deal.subdivision.site_costs} />
                </div>
              )}
            </Collapse>
          )}
          {deal.parcels && deal.parcels.count > 1 && (
            <Collapse title={`Parcels · ${deal.parcels.count} Lots (Assembled)`} testid="portal-parcels" icon={MapPin}>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="portal-parcels-total-sqft">
                  <div className="text-[10px] uppercase tracking-wider text-white/40">Combined Lot Size</div>
                  <div className="text-base font-bold text-white mt-1">{deal.parcels.total_sqft}</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="portal-parcels-total-acres">
                  <div className="text-[10px] uppercase tracking-wider text-white/40">Combined Acres</div>
                  <div className="text-base font-bold text-white mt-1">{deal.parcels.total_acres}</div>
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full text-sm" data-testid="portal-parcels-table">
                  <thead>
                    <tr className="text-left text-white/40 text-[11px] uppercase tracking-wider border-b border-white/10">
                      <th className="px-4 py-2.5 font-medium">Parcel</th>
                      <th className="px-4 py-2.5 font-medium">APN</th>
                      <th className="px-4 py-2.5 font-medium text-right">Lot Size (Sq Ft)</th>
                      <th className="px-4 py-2.5 font-medium text-right">Acres</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deal.parcels.items.map((p, i) => (
                      <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`portal-parcel-row-${i}`}>
                        <td className="px-4 py-2.5 text-white/90 font-medium">{i + 1}{i === 0 ? " · Primary" : ""}</td>
                        <td className="px-4 py-2.5 text-white/80">{p.apn}</td>
                        <td className="px-4 py-2.5 text-right text-white/80">{p.lot_size_sqft}</td>
                        <td className="px-4 py-2.5 text-right text-white/80">{p.lot_size_acres}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Collapse>
          )}
          <Section title="Property Details" rows={deal.property_details} testid="portal-property-details" icon={Building2} />
          <Section title="Lot Information" rows={deal.lot_information} testid="portal-lot-info" icon={MapPin} />
          <Section title="Zoning & Usage" rows={deal.zoning} testid="portal-zoning" icon={Scale} />
          {deal.home_comp_summary && (
            <Section title="Home Comparable Summary" rows={deal.home_comp_summary} testid="portal-home-comps" icon={BarChart3} />
          )}
          {deal.land_comp_summary && (
            <Section title="Land Comparable Summary" rows={deal.land_comp_summary} testid="portal-land-comps" icon={BarChart3} />
          )}

          {/* Documents */}
          {deal.documents && deal.documents.length > 0 && (
            <Collapse title="Documents & Attachments" testid="portal-documents" icon={FileText} right={`${deal.documents.length} file${deal.documents.length > 1 ? "s" : ""}`}>
              <div className="divide-y divide-white/5">
                {deal.documents.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => window.open(`${BACKEND}${d.url}`, "_blank", "noopener,noreferrer")}
                    className="w-full flex items-center justify-between gap-3 py-3 text-left group"
                    data-testid={`portal-doc-${d.id}`}
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <FileText className="h-4 w-4 text-[#27BEAA] shrink-0" />
                      <span className="text-sm text-white/90 truncate group-hover:text-white">{d.filename}</span>
                      <span className="text-[11px] uppercase tracking-wide text-white/30 shrink-0">{d.category}</span>
                    </span>
                    <Eye className="h-4 w-4 text-white/40 group-hover:text-[#27BEAA] shrink-0" />
                  </button>
                ))}
              </div>
            </Collapse>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 mt-6">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between text-white/30 text-xs">
          <img src={LOGO} alt="ARQITEK" className="h-5 w-auto object-contain opacity-60" />
          <span>© {new Date().getFullYear()} ARQITEK. Confidential — for prospective investors.</span>
        </div>
      </footer>
    </div>
  );
}
